<script>
import Vue from 'vue'
import date from '../mixins/date'
import MessagesSingle from './MessagesSingle.vue'
import UserResource from '../resources/user'
import MessageResource from '../resources/message'
import DeveloperProfileResource from '../resources/developerprofile'

export default Vue.extend({
    components: {
        MessagesSingle
    },
    mixins: [date],
    props: {
        userResource: {
            type: UserResource,
            required: true,
        },
        value: {
            type: Number,
            default: 0
        },
        refreshInterval: {
            type: Number,
            default: 6e4
        }
    },
    computed: {
        selectedUserResourceRecentsIndex() {
            return this.recentUserResources.indexOf(this.selectedUserResource)
        },
        unreadMessageResources() {
            return this.messageResources.filter((resource) => {
                return !resource.attributes.read
            })
        }
    },
    data() {
        return {
            loading: true,
            recentUserResources: [],
            selectedUserResource: null,
            messageResources: [],
            senderProfilesIndexed: {},
            messageTo: {},
            unreadCountTotal: 0,
            unreadCountFetchInterval: null
        }
    },
    watch: {
        async selectedUserResource() {
            if(!this.selectedUserResource) {
                return false
            }

            let page = await MessageResource.thread(this.selectedUserResource.id)
            this.messageResources = page.resources
            this.scrollMessageBottom()
        },
        messageResources() {
            this.scrollMessageBottom()
        },
        unreadCountTotal() {
            this.$emit('input', this.unreadCountTotal)
        }
    },
    methods: {
        async loadRecents() {
            this.loading = true
            let recentResult = await UserResource.getMessagesRecent()
            this.recentUserResources = recentResult.resources

            for(let userResource of this.recentUserResources) {
                let profile = await userResource.getProfile()
                
                if(profile) {
                    await profile.resolveRelated({ managers: ['image'] })
                    this.senderProfilesIndexed[userResource.id] = profile
                }
            }

            this.selectedUserResource = this.recentUserResources[0]
            
            await this.updateUnreadCount()

            this.loading = false
        },
        async sendMessage(userId) {
            let content = this.messageTo[userId]

            if(content) {
                let message = new MessageResource({ content, to: userId })
                await message.save()
                this.messageResources.push(message)
                // Delete index of recents but then move it to the top
                this.recentUserResources.splice(this.selectedUserResourceRecentsIndex, 1)
                this.recentUserResources.unshift(this.selectedUserResource)
                // Then clear the text box
                this.messageTo[userId] = ''
            }
        },
        async updateUnreadCount() {
            this.unreadCountTotal = await MessageResource.unreadCount(this.userResource.id)
        },
        async selectRecentUserResource(userResource) {
            this.selectedUserResource = userResource
            let promises = []
            
            for(let messageResource of this.unreadMessageResources) {
                if(messageResource.attributes.to === this.userResource.id) {
                    promises.push(messageResource.read())
                }
            }

            await Promise.all(promises)
        },
        scrollMessageBottom() {
            this.$nextTick(() => {
                if(!this.$refs.messages) {
                    return false
                }

                this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight
            })
        },
        stopFetchingUnreads() {
            clearInterval(this.unreadCountFetchInterval)
        },
        startFetchingUnreads() {
            this.unreadCountFetchInterval = setInterval(() => this.updateUnreadCount(), this.refreshInterval)
        },
        getImageSourceForUser(userResource) {
            if(!userResource) {
                return ''
            }

            let profile = this.senderProfilesIndexed[userResource.id]
            
            if(!profile) {
                return ''
            }

            return String(profile.get('image')) || ''
        }
    },
    mounted() {
        this.loadRecents()
        //this.startFetchingUnreads()
    }
})
</script>

<template>
    <div class="messages-wrapper">
        <!-- <b-row>
            <b-col>
                <b-button @click="loadRecents" size="sm" class="float-right"><b-icon icon="arrow-counterclockwise" /> Reload</b-button>
            </b-col>
        </b-row> -->
        <div v-if="!loading" class="h-100">
            <b-row v-if="recentUserResources.length > 0" class="recents">
                <b-col>
                    <div v-for="(userResource, i) in recentUserResources"
                        :key="i"
                        :class="selectedUserResource.id == userResource.id ? 'recents-link selected' : 'recents-link'"
                        @click="selectRecentUserResource(userResource)">
                        <b-avatar :src="getImageSourceForUser(userResource)" />
                        <strong>{{ userResource }}</strong>
                    </div>
                </b-col>
                <b-col class="messages-inner">
                    <div class="messages" ref="messages">
                        <messages-single v-for="(messageResource, i) in messageResources" :key="i" :messageResource="messageResource" :mine="messageResource.attributes.sender === userResource.id" />
                    </div>
                    <div class="reply">
                        <form @submit.prevent="sendMessage(selectedUserResource.id)">
                            <textarea v-model="messageTo[selectedUserResource.id]" />
                            <b-button type="submit">Send</b-button>
                        </form>
                    </div>
                </b-col>
            </b-row>

            <p v-else>
                <b-col>
                    <b-card>
                        <h6>Messages List</h6>
                        <p>You don't have any messages.</p>
                    </b-card>
                </b-col>
            </p>
        </div>

        <div v-else>
            <b-col>
                <h6>Loading messages...</h6>
                <b-skeleton animation="wave" width="85%"></b-skeleton>
                <b-skeleton animation="wave" width="55%"></b-skeleton>
                <b-skeleton animation="wave" width="70%"></b-skeleton>
                <b-skeleton animation="wave" width="85%" class="mt-3"></b-skeleton>
                <b-skeleton animation="wave" width="55%"></b-skeleton>
                <b-skeleton animation="wave" width="70%"></b-skeleton>
            </b-col>
        </div>
    </div>
</template>

<style lang="scss" scoped>
$font-size: 0.95rem;
$shade-color: rgb(241, 241, 241);
$reply-height-int: 120;
$reply-height: $reply-height-int + px;
$reply-height-full: $reply-height-int + 10 + px;
$user-select-height: 4rem;

.messages-wrapper {
    height: 100%;
    overflow: hidden;
}

.recents {
    border-top: 1px solid $shade-color;
    height: 100%;

    .recents-link {
        line-height: $user-select-height;
        height: $user-select-height;
        overflow: hidden;
        cursor: pointer;
        border-bottom: 1px solid $shade-color;
        padding: 0 10px;

        strong {
            margin-left: 10px;
        }

        &:hover {
            background-color: $shade-color;
        }

        &.selected {
            background-color: $shade-color;
            border: none;
            border-left-width: 3px;
            border-left: inset;
            border-color: #ce75b4;
        }
    }
}

.messages {
    height: 100%;
    overflow-y: scroll;
    position: relative;
    padding: 10px 10px $reply-height-full 10px;
}

.messages-inner {
    height: 100%;
    position: relative;

    .reply {
        background-color: $shade-color;
        width: 86%;
        height: $reply-height;
        position: absolute;
        bottom: 0;
        right: 8%;

        form {
            textarea {
                width: 100%;
                height: 50%;
                border: 1px solid rgb(241, 241, 241);
                font-size: $font-size;
            }
        }
    }
}

@import "~bootstrap/scss/functions";
@import "~bootstrap/scss/variables";
@import "~bootstrap/scss/mixins";

@include media-breakpoint-down(sm) {
    .recents {
        zoom: 0.8;
    }
}
</style>
